import React, { useEffect, useState } from "react";
import "./BootSplash.css";

/* BootSplash — LHC_Platform_Animated.html, in React (2026-08-26 user: "for
 * animation is always shown on refresh page always ... also the animation it's
 * for like 2 sec").
 *
 * ALWAYS on a page load, never on a client-side route change: the component
 * mounts once at the App root and retires itself, so navigating between /store
 * and /studio does not replay it, while F5 does — which is exactly what
 * "shown on refresh, always" means. No sessionStorage gate for the same
 * reason: a remembered "already seen" would swallow the next refresh.
 *
 * TIMING (2 s total): the wordmark wipes in over 1.5 s from .35 s, the rule
 * grows under it at 1.55 s, then the whole thing cross-fades out over 420 ms
 * from 1.58 s and unmounts at 2 s. The original's 5–6 s breathe/glow loops are
 * dropped — they only exist for a page that stays on screen.
 *
 * It never blocks the app: the real UI renders underneath from the first
 * frame, the overlay is pointer-events:none, and a reduced-motion viewer gets
 * a static mark for a beat instead of the wipe.
 */
/* 2026-08-30 user: "replace the animation with THIS" — the uploaded file's
 * own timings restored in full: wordmark wipe 1.5s from .35s, the long rule
 * growing 2.6s from 1.4s (done at 4.0s), halo fade + breathe + glow live
 * while it shows. Fade out right after the line lands. */
const OUT_AT = 3950;   // ms — start the fade (the line finishes at 4.0s)
const GONE_AT = 4400;  // ms — unmount

/* INSIDE THE APP, FAST (2026-09-30 user: "make the animation a bit faster — only when you open
 * the configurator it should be like it is; when you are inside, no need to be this slow").
 * Client-side navigation never replays the splash, so a play while "inside" IS a page reload —
 * F5, the build watcher applying a deploy, the Retry button. The FIRST arrival in a tab (opening
 * the platform, a fresh tab) and the sign-in replay earn the uploaded animation in full; a reload
 * inside the app plays a QUICK cut of the same wipe and rule (~1.1 s) so the brand still marks
 * the refresh without holding the workspace. Per-tab memory only (sessionStorage): a new tab is
 * a fresh arrival again, and nothing is ever swallowed. */
const SEEN_KEY = "lhc_boot_seen";
const FULL = { out: OUT_AT, gone: GONE_AT };
const QUICK = { out: 850, gone: 1150 };
function seenInThisTab() {
  try { return typeof sessionStorage !== "undefined" && sessionStorage.getItem(SEEN_KEY) === "1"; }
  catch (e) { return false; }
}
function markSeen() {
  try { if (typeof sessionStorage !== "undefined") sessionStorage.setItem(SEEN_KEY, "1"); } catch (e) { /* private mode */ }
}

/* LOGGED-IN ONLY (2026-08-30 user: "it should appear only when you log in
 * and when you refresh AFTER you log in — if you're not logged in it should
 * NOT appear"): the boot play is gated on the auth token; the login replay
 * event still always plays (it only fires on a successful sign-in). */
function isAuthed() {
  try { return !!(typeof localStorage !== "undefined" && localStorage.getItem("lhc_token")); }
  catch (e) { return false; }
}

/* …AND INSIDE THE APP ONLY (2026-09-07 user: "the animation shouldn't show up on
 * the site, it only shows up when you log in and you are inside configurator").
 * The LHC public site at "/" is a marketing site for everyone — a signed-in dealer
 * browsing it still got the platform boot beat on every refresh, because the token
 * was all that was checked. The brand animation belongs to the signed-in workspaces,
 * so the boot play now also requires an app surface. These are the app route prefixes
 * from App.js; everything else ("/", every product world, /partners/*) is the public
 * site. urlMask's short display paths (/store/client, /studio, /sales/order …) still
 * start with one of these. */
const APP_PREFIXES = ["/store", "/studio", "/admin", "/superadmin", "/admin-legacy", "/catalog",
  "/sales", "/manufacturer", "/installer", "/client-portal", "/dealer"];
function isAppSurface() {
  try {
    const p = window.location.pathname;
    return APP_PREFIXES.some((x) => p === x || p.startsWith(x + "/"));
  } catch (e) { return false; }
}

/* REPLAY (2026-08-26 user: "also add this animation when u log in too"). Any
 * surface can ask for the brand beat again — signing in is the moment that
 * earns it, because the app is swapping to a whole new workspace. Fired
 * through a plain event so nothing has to thread a prop down to the login
 * form or hold the splash in app state. */
export const BOOT_SPLASH_EVENT = "lhc:boot-splash";
export function playBootSplash() {
  try { window.dispatchEvent(new CustomEvent(BOOT_SPLASH_EVENT)); } catch (e) { /* no window */ }
}
/** Total time the animation occupies — callers that want to navigate "after
 *  the animation" should use this rather than guessing (the sign-in replay is always full). */
export const BOOT_SPLASH_MS = GONE_AT;
export const BOOT_SPLASH_QUICK_MS = QUICK.gone;

export default function BootSplash() {
  // in → out → (unmounted). Logged-out loads never play, and neither does the public site.
  const [phase, setPhase] = useState(() => (isAuthed() && isAppSurface() ? "in" : "gone"));
  const [run, setRun] = useState(0);          // bumped per replay
  // a reload while already inside the app (this tab has played once) gets the quick cut
  const [quick, setQuick] = useState(() => seenInThisTab());

  useEffect(() => {
    if (phase === "gone") return undefined;    // logged-out boot: nothing to time
    markSeen();
    const t = quick ? QUICK : FULL;
    const a = setTimeout(() => setPhase("out"), t.out);
    const b = setTimeout(() => setPhase("gone"), t.gone);
    return () => { clearTimeout(a); clearTimeout(b); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  useEffect(() => {
    // signing in earns the full beat — the app is swapping to a whole new workspace
    const replay = () => { setQuick(false); setPhase("in"); setRun((n) => n + 1); };
    window.addEventListener(BOOT_SPLASH_EVENT, replay);
    return () => window.removeEventListener(BOOT_SPLASH_EVENT, replay);
  }, []);

  if (phase === "gone") return null;
  return (
    <div key={run} className={"lhc-boot" + (phase === "out" ? " out" : "") + (quick ? " lhc-boot--quick" : "")}
      data-testid="boot-splash" data-cut={quick ? "quick" : "full"} aria-hidden="true">
      <div className="lhc-boot-halo" />
      <div className="lhc-boot-stack">
        <div className="lhc-boot-mark">
          <div className="lhc-boot-brand">LHC <span className="p">Platform</span></div>
        </div>
        <div className="lhc-boot-line" />
      </div>
    </div>
  );
}
